import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Grid, Typography } from '@material-ui/core';

import {
    JOURNEY_TRACKER_VISIBLE_ACTIONS,
    JOURNEY_TRACKER_AFTER_FINISH_TOUR_ACTIONS,
} from 'yeself-common/typings/application/ApplicationEnum';
import { FEATURE_FLAG_NAMES } from 'yeself-common/typings/FeatureFlags';

import { selectFeatureFlags } from 'yeself-client-common/featureFlags/FeatureFlagsSelector';

import LocalizedPlainStringField from 'yeself-client-common/form/components/LocalizedPlainStringField';

import { omitKeyValueObjectByValue } from 'yeself-client-common/utils/CommonUtils';

import { selectFallbackLanguage } from 'Console/data/applicationDetail/settings/SettingsSelector';

import { selectContentLanguage } from 'Console/data/app/selectors';

import JourneyDropdownMenuForm from './JourneyDropdownMenu';

const styles = (theme) => ({
    editGuideFormWrapper: {
        marginTop: theme.typography.pxToRem(10),
        marginBottom: theme.typography.pxToRem(30),
        position: 'relative',
    },
    buttonsWrapper: {
        marginTop: theme.typography.pxToRem(36),
    },
    title: {
        fontSize: theme.typography.pxToRem(16),
        fontWeight: 600,
    },
    languageWrapper: {
        marginLeft: theme.typography.pxToRem(15),
        height: theme.typography.pxToRem(61),
    },
    formLabel: {
        flexBasis: '35%',
        marginRight: '5%',
        fontSize: theme.typography.pxToRem(14),
        [theme.breakpoints.down('sm')]: {
            'margin-right': 0,
        },
    },
    items: {
        flexWrap: 'nowrap',
        paddingBottom: theme.typography.pxToRem(20),
        [theme.breakpoints.down('md')]: {
            flexDirection: 'column',
            alignItems: 'flex-start',
            'justify-content': 'flex-start',
            'margin-right': 0,
            paddingBottom: theme.typography.pxToRem(20),
        },
    },
    languageSelector: {
        position: 'absolute',
        top: theme.typography.pxToRem(-10),
        right: 0,
        width: 'auto',
    },
    formHeader: {
        marginBottom: theme.typography.pxToRem(45),
    },
    formInput: {
        width: '60%',
        [theme.breakpoints.between('xs', 'md')]: {
            width: '100%',
            marginBottom: theme.typography.pxToRem(20),
        },
    },
    formName: {
        width: '35%',
        marginRight: '5%',
        [theme.breakpoints.between('xs', 'md')]: {
            width: '100%',
            marginRight: 0,
            paddingBottom: theme.typography.pxToRem(10),
        },
    },
    textField: {
        marginTop: 0,
        marginBottom: 0,
        background: '#FAFAFA',
        color: 'rgba(0, 0, 0, 0.87)',
        boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.24), 0px 0px 2px rgba(0, 0, 0, 0.12)',
        borderRadius: 2,
    },
    menu: {
        width: 250,
    },
    cssSelect: {
        '&:focus': {
            background: 'inherit',
        },
    },
    cssSelectRoot: {
        backgroundColor: 'inherit',
        borderRadius: 'inherit',
        '&:hover': {
            backgroundColor: 'inherit',
            borderBottom: 'none',
        },
        '&$focused': {
            backgroundColor: 'inherit',
        },
        '&$disabled': {
            backgroundColor: 'rgba(0, 0, 0, 0.03)',
        },
    },
    cssUnderline: {
        '&:before': {
            borderBottom: 'none',
        },
        '&:after': {
            borderBottom: 'none',
        },
        '&:hover:not([disabled]):not([focused]):not([error]):before': {
            borderBottom: `inherit !important`,
        },
    },
});

const CreateNewJourney = ({
    intl,
    classes,
    journeyToEdit,
    title,
    form: { setFieldsValue, getFieldDecorator },
}) => {
    const contentLanguage = useSelector(selectContentLanguage);
    const fallbackLanguage = useSelector(selectFallbackLanguage);
    const featureFlags = useSelector(selectFeatureFlags);

    useEffect(() => {
        if (journeyToEdit) {
            setFieldsValue({
                name: journeyToEdit.content.name,
                afterJourneyComplete: journeyToEdit.actions.afterJourneyComplete,
                afterTourInJourneyComplete: journeyToEdit.actions.afterTourInJourneyComplete,
            });
        }
    }, [journeyToEdit, setFieldsValue]);

    const filteredJourneyTrackerActions = omitKeyValueObjectByValue(
        JOURNEY_TRACKER_AFTER_FINISH_TOUR_ACTIONS,
        JOURNEY_TRACKER_AFTER_FINISH_TOUR_ACTIONS.OPEN_CONTEXT_HELP
    );

    return (
        <Grid container className={classes.editGuideFormWrapper}>
            <Grid
                container
                direction="row"
                alignItems="center"
                justify="space-between"
                className={classes.formHeader}
            >
                {title && (
                    <Grid item>
                        <Typography className={classes.title}>{title}</Typography>
                    </Grid>
                )}
            </Grid>

            <Grid container>
                <Grid
                    id="yeself-createNewJourney"
                    container
                    direction="row"
                    alignItems="center"
                    className={classes.items}
                >
                    <Typography className={classes.formLabel} variant="body1">
                        <FormattedMessage id="Journey.title" defaultMessage="Title" />
                    </Typography>

                    <Grid className={classes.formInput}>
                        {getFieldDecorator('name', {
                            initialValue: journeyToEdit ? journeyToEdit.content.name : {},
                            preserve: true,
                        })(
                            <LocalizedPlainStringField
                                locale={contentLanguage}
                                fallbackLocale={fallbackLanguage.value}
                                placeholder={intl.formatMessage({
                                    id: 'Journeys.title.placeholder',
                                })}
                            />
                        )}
                    </Grid>
                </Grid>

                {getFieldDecorator('afterJourneyComplete', {
                    initialValue: journeyToEdit
                        ? journeyToEdit.actions.afterJourneyComplete
                        : 'noAction',
                    preserve: true,
                })(
                    <JourneyDropdownMenuForm
                        classNames={{
                            formInput: classes.formInput,
                            itemTextClassName: classes.formName,
                            textField: classes.textField,
                            menu: classes.menu,
                            cssSelect: classes.cssSelect,
                            cssSelectRoot: classes.cssSelectRoot,
                            cssUnderline: classes.cssUnderline,
                        }}
                        formattedMessage={{
                            id: 'Journey.visible.action.title',
                            defaultMessage: 'Action after journey is complete',
                        }}
                        menuItems={{
                            options: JOURNEY_TRACKER_VISIBLE_ACTIONS,
                            id: 'Journey.visible.action.options',
                        }}
                    />
                )}

                {getFieldDecorator('afterTourInJourneyComplete', {
                    initialValue: journeyToEdit
                        ? journeyToEdit.actions.afterTourInJourneyComplete
                        : featureFlags[FEATURE_FLAG_NAMES.CONTEXT_HELP]
                        ? JOURNEY_TRACKER_AFTER_FINISH_TOUR_ACTIONS.OPEN_CONTEXT_HELP
                        : JOURNEY_TRACKER_AFTER_FINISH_TOUR_ACTIONS.NO_ACTION,
                    preserve: true,
                })(
                    <JourneyDropdownMenuForm
                        classNames={{
                            formInput: classes.formInput,
                            itemTextClassName: classes.formName,
                            textField: classes.textField,
                            menu: classes.menu,
                            cssSelect: classes.cssSelect,
                            cssSelectRoot: classes.cssSelectRoot,
                            cssUnderline: classes.cssUnderline,
                        }}
                        formattedMessage={{
                            id: 'Journey.afterFinishTour.action.title',
                            defaultMessage: 'Action after a tour in a journey is complete',
                        }}
                        menuItems={{
                            options: !featureFlags[FEATURE_FLAG_NAMES.CONTEXT_HELP]
                                ? filteredJourneyTrackerActions
                                : JOURNEY_TRACKER_AFTER_FINISH_TOUR_ACTIONS,
                            id: 'Journey.afterFinishTour.action.options',
                        }}
                    />
                )}
            </Grid>
        </Grid>
    );
};

export default withStyles(styles)(injectIntl(CreateNewJourney));
